/**
 * The fold: ledger → claims.
 *
 * Recomputed at session boundaries only, never mid-session — the model must not
 * be able to twitch while someone is working.
 */

import {
  PRIOR_MASS, claimKey, domainOf, isIndexed, isKnob, scopeChain, scopeId,
  type KnobId, type Scope, type ScopeLevel,
} from './knobs.js';
import { decayedWeight, type SignalEvent, type SignalClass } from './ledger.js';

export interface Claim {
  knob: KnobId;
  index?: string;
  scope: Scope;
  /** Posterior over the knob's domain. Sums to 1. */
  posterior: Record<string, number>;
  /** Decayed evidence mass at this level only, excluding inherited prior. */
  evidence: number;
  classes: SignalClass[];
  sessions: number;
  days: number;
  confidence: number;
  stated: boolean;
  frozen: boolean;
  /** Values that lost an argument. See `gates.ts`. */
  refractory: Record<string, { until: number; multiplier: number; retired: boolean; clockFrom: number }>;
  because: Array<{ eventId: string; at: number; class: SignalClass }>;
}

export type Claims = ReadonlyMap<string, Claim>;

/** Uniform over the domain — the honest starting position. */
const uniform = (domain: readonly string[]): Record<string, number> =>
  Object.fromEntries(domain.map((v) => [v, 1 / domain.length]));

function normalize(scores: Record<string, number>): Record<string, number> {
  const total = Object.values(scores).reduce((a, b) => a + Math.max(0, b), 0);
  if (total <= 0) return uniform(Object.keys(scores));
  return Object.fromEntries(Object.entries(scores).map(([k, v]) => [k, Math.max(0, v) / total]));
}

function entropy(p: Record<string, number>): number {
  return -Object.values(p).reduce((s, x) => (x > 0 ? s + x * Math.log(x) : s), 0);
}

interface Bucket {
  weights: Record<string, number>;
  classes: Set<SignalClass>;
  sessions: Set<string>;
  days: Set<string>;
  stated: boolean;
  frozen: boolean;
  refractory: Claim['refractory'];
  because: Claim['because'];
}

const emptyBucket = (domain: readonly string[]): Bucket => ({
  weights: Object.fromEntries(domain.map((v) => [v, 0])),
  classes: new Set(), sessions: new Set(), days: new Set(),
  stated: false, frozen: false, refractory: {}, because: [],
});

/**
 * `knob -> value -> probability`, the base a retrieval snapshot aggregates to.
 *
 * Decision 0040 names three values that were all called "the pool prior":
 * `PoolGenome` (what `project()` admits), `RetrievedGenome` (a genome plus its
 * id and request-relative score — the value still called `PoolPrior` in
 * `types.ts`, which phase 2 renames), and this one, which is the only one
 * `fold()` consumes.
 */
export type PoolTastePrior = Readonly<Record<string, Record<string, number>>>;

/**
 * The three knobs a pool may vote on, and the only three.
 *
 * Decision 0040 names them and forbids the rest: `collection.flow`, `media.fit`
 * and indexed `role.presence` among them, because *"deriving any of those from
 * `structure` would introduce a second, implicit projection policy."*
 *
 * Lives HERE rather than beside the retriever, because this is the boundary
 * that can be handed an arbitrary `PoolTastePrior` — `Record<string, …>` is a
 * public type, and a caller is free to put any key in it. The list was in
 * `pool-prior.ts`, where it governed what a genome VOTES on and could say
 * nothing about what this function accepts.
 */
export const POOLED_KNOBS = ['density', 'typography', 'palette'] as const;
const POOLED = new Set<string>(POOLED_KNOBS);

export interface FoldOptions {
  now: number;
  /**
   * The pool's categorical base per knob. Read once at signup and on explicit
   * request, never continuously — otherwise a popular genome drifts everyone's
   * interface, which is churn caused by strangers.
   *
   * This said *pseudo-count* until decision 0040, and the word was the whole of
   * a defect that had not happened yet: a pseudo-count is evidence, and evidence
   * accumulates, so a pool of a hundred genomes would out-argue the person whose
   * interface it is. The code never did that — `normalize` turns the base into a
   * distribution and `PRIOR_MASS[level]` alone decides how loudly it speaks, so
   * ten copies of one genome have always been worth exactly one. Only the
   * comment claimed otherwise, and it is the comment somebody builds against.
   *
   * 0040: *"`PoolTastePrior` is a categorical base distribution, not accumulated
   * evidence and not a bag of pseudo-counts. `PRIOR_MASS` alone determines how
   * strongly that base competes with a person's evidence."*
   */
  poolPrior?: PoolTastePrior;
  /** Fraction of accepted changes still standing at 30 days. The model's
   *  opinion of itself; scales all confidence. */
  reliability?: number;
}

/**
 * A pool prior that is not a categorical distribution over the knob's domain.
 *
 * Named separately from `InvalidRetrievalScore` because the two guard different
 * inputs — that one a retrieved genome's score, this one a folded distribution —
 * and a caller wants to know which of its two pool inputs was wrong.
 */
export class InvalidPoolPrior extends Error {
  constructor(knob: string, value: string, probability: unknown) {
    super(value
      ? `pool prior ${knob}.${value} is ${String(probability)}, not a probability `
        + 'in the knob\u2019s domain'
      : `pool prior for ${knob} is ${String(probability)}, not a distribution`);
    this.name = 'InvalidPoolPrior';
  }
}

export function fold(ledger: readonly SignalEvent[], opts: FoldOptions): Claims {
  const now = opts.now;
  const reliability = opts.reliability ?? 1;
  const buckets = new Map<string, { knob: KnobId; index?: string; scope: Scope; b: Bucket }>();

  for (const ev of ledger) {
    if (ev.retractedAt) continue;
    for (const obs of ev.observations) {
      // Fail closed on stale vocabulary — and a knob can go stale two ways.
      // A withdrawn VALUE was always handled; a withdrawn KNOB was not, and
      // `domainOf` returns `undefined` for one, so `domain.includes` threw.
      // That matters because the ledger is durable: decision 0036 withdrew
      // `role.extent` while stored ledgers already carried its observations,
      // and every fold for anyone who had used the slider would have crashed.
      // A registry that can be withdrawn from needs a fold that survives it.
      const domain = domainOf(obs.knob) as readonly string[] | undefined;
      if (!domain?.includes(obs.value)) continue;
      const key = claimKey(obs.knob, ev.scope, obs.index);
      let entry = buckets.get(key);
      if (!entry) {
        entry = {
          knob: obs.knob, scope: ev.scope, b: emptyBucket(domain),
          ...(obs.index !== undefined ? { index: obs.index } : {}),
        };
        buckets.set(key, entry);
      }
      const b: Bucket = entry.b;
      const w = decayedWeight(ev, obs, now);

      if (ev.class === 'correction') {
        // Not "slightly less likely" — retracted. And the clock resets, so old
        // evidence for that value cannot be re-litigated.
        b.weights[obs.value] = 0;
        b.refractory[obs.value] = {
          until: now + 90 * 86_400_000, multiplier: 2,
          retired: b.refractory[obs.value]?.retired ?? false, clockFrom: now,
        };
      } else {
        b.weights[obs.value] = (b.weights[obs.value] ?? 0) + w;
      }

      if (ev.class === 'stated') b.stated = true;
      if (ev.class === 'pin') b.frozen = true;
      if (ev.class === 'release') b.frozen = false;

      if (ev.class === 'revert') {
        const prior = b.refractory[obs.value];
        b.refractory[obs.value] = {
          until: now + 180 * 86_400_000,
          multiplier: prior ? 2 : 2,
          // Two reversals and the model may never propose it unsolicited again.
          // It is allowed to lose an argument twice.
          retired: Boolean(prior),
          clockFrom: now,
        };
      }

      b.classes.add(ev.class);
      b.sessions.add(ev.sessionId);
      b.days.add(new Date(ev.at).toISOString().slice(0, 10));
      b.because.push({ eventId: ev.id, at: ev.at, class: ev.class });
    }
  }

  /**
   * Cold start: a pool base with no personal evidence still produces a claim.
   *
   * Every bucket above comes from a ledger EVENT, so an empty ledger produced no
   * buckets, and a `poolPrior` was therefore read by nothing. The pool could not
   * affect a first layout at all — which is the whole of what decision 0039's
   * *"Suggest from the pool"* gesture is for.
   *
   * An empty bucket is all it takes, and the arithmetic below already says the
   * right thing about it. With no weights, `positives` is 0 and every score is
   * `m * baseNorm[v] / m` — the pool's own distribution, exactly. Evidence is 0,
   * `classes` is empty so the two-class factor is 0, and confidence is therefore
   * 0 as well. That is decision 0040's requirement met by construction rather
   * than by a special case: *"any resulting pool-only claim has `evidence: 0`,
   * no signal classes, no sessions or days, and confidence zero … ordinary
   * learned taste paths must not mistake that pool-only claim for something the
   * person taught Morph."*
   *
   * Global only, and only for the three knobs `0040` names.
   *
   * This tested `!isIndexed(knob)` and said so: *"`0040` restricts pool votes
   * to `density`, `typography` and `palette`, none of which are indexed"*. True
   * and not the same rule. @Codex, P2 on #184: *"When a caller supplies a
   * `PoolTastePrior` containing another live non-indexed knob, such as
   * `collection.flow`, this loop materializes a pool-only claim for it because
   * the type permits arbitrary keys and the guard only checks `isKnob`. That
   * lets the public fold boundary accept exactly the implicit flow/media votes
   * decision 0040 forbids."*
   *
   * A guard that tests a PROPERTY the allowed set happens to share is a guard
   * about the wrong question, and it was written with the right sentence
   * directly above it. `POOLED_KNOBS` is the rule; not being indexed is a fact
   * about its members.
   */
  if (opts.poolPrior) {
    /**
     * Every supplied probability is a probability — checked before it folds.
     *
     * @Codex, P2 on #184: *"When a public or corrupted `PoolTastePrior`
     * contains `NaN` or `Infinity`, spreading it into the base here makes
     * `normalize()` return `NaN` for every posterior value rather than refusing
     * the invalid distribution. `uniqueArgmax()` then treats the `NaN`
     * difference as non-tied and returns the first domain value — for example,
     * a `density` prior containing `airy: NaN` manufactures `airy` as the
     * winner."*
     *
     * Two channels, and the second is the quieter one. A non-finite value makes
     * every posterior `NaN` and hands the win to whichever value the domain was
     * typed in first — the same `argmax`-resolves-a-tie-by-declaration-order
     * defect decision `0040`'s majority guard was written for, reached by
     * arithmetic instead of by a tie. And a key OUTSIDE the domain is never
     * scored but is still spread into `normalize()`, so it moves the
     * denominator: an influence on the result from a value that cannot win.
     *
     * Refused rather than repaired, which is what `aggregate()` already does
     * one function away for exactly this class of input — `InvalidRetrievalScore`
     * on a score that is not a number in [0,1]. A caller that got its prior from
     * `aggregate()` cannot reach this; a caller that built one by hand should
     * hear about it rather than get a plausible layout built on `NaN`.
     */
    for (const [knob, distribution] of Object.entries(opts.poolPrior)) {
      /**
       * Every REAL knob, not only the three the pool may vote on.
       *
       * A key that is not a knob at all is left alone: nothing reads it, the
       * loop below refuses to bucket it, and `buckets` only ever holds real
       * knobs.
       *
       * But a real knob outside `POOLED` is a different case, and the narrower
       * guard I first wrote here missed it. The bucketing loop below skips
       * `collection.flow`, so a prior for it materializes no pool-only
       * claim — and the `base` lookup further down reads `poolPrior[knob]` for
       * whatever buckets EXIST, so a ledger carrying its own `collection.flow`
       * event opens the same `NaN` channel through a knob the pool was never
       * allowed to vote on. Checking the numbers on every knob closes both
       * routes; whether that second read should exist at all is decision
       * `0040`'s question and not this guard's.
       */
      if (!isKnob(knob)) continue;
      if (!distribution || typeof distribution !== 'object') {
        throw new InvalidPoolPrior(knob, '', distribution);
      }
      const domain = domainOf(knob);
      let mass = 0;
      for (const [value, probability] of Object.entries(distribution)) {
        if (typeof probability !== 'number' || !Number.isFinite(probability)
            || probability < 0 || probability > 1) {
          throw new InvalidPoolPrior(knob, value, probability);
        }
        // Scored only over the domain, but `normalize()` sums everything — so a
        // value the knob does not have is a thumb on the denominator.
        if (!domain.includes(value)) throw new InvalidPoolPrior(knob, value, probability);
        mass += probability;
      }
      /**
       * And it must be a DISTRIBUTION, not merely a bag of probabilities.
       *
       * @Codex, P2 on #184, on the first version of this guard: *"an externally
       * supplied or corrupted prior such as `{ density: { airy: 0, balanced: 0,
       * dense: 2 } }` … every entry passes this check even though `2` is not a
       * probability and the values do not total one. The later normalization
       * silently converts it into a valid-looking posterior and can install a
       * taste winner instead of raising `InvalidPoolPrior`; an all-zero or
       * over-mass input is likewise accepted."*
       *
       * Right, and it is this guard's own defect one round later: it was written
       * against `NaN` and checked every entry in isolation, which is a test of
       * the values rather than of the thing they are supposed to make. Decision
       * `0040` asks for a categorical distribution, and a categorical
       * distribution is a property of the whole.
       *
       * `normalize()` is what makes the omission dangerous rather than untidy:
       * it will turn any non-negative bag into something that sums to one, so
       * an over-mass prior does not fail anywhere — it just wins.
       *
       * The tolerance is for float summation only. `aggregate()` builds these
       * through `normalize()`, so a legitimate prior totals one to within a few
       * ULPs; `1e-9` is six orders of magnitude looser than that and still
       * refuses `0` and `2`.
       */
      if (Math.abs(mass - 1) > 1e-9) throw new InvalidPoolPrior(knob, '', mass);
      /**
       * And it must name EVERY value in the domain, not merely sum to one.
       *
       * @Codex, P2 on #184: *"a sparse unit-mass distribution such as
       * `{ density: { dense: 1 } }` … the validation accepts it because every
       * present value is valid and the mass totals one, but the spread fills
       * the absent categories with uniform probability before normalizing. The
       * requested certain `dense` prior consequently becomes approximately
       * `{ airy: 0.2, balanced: 0.2, dense: 0.6 }`."*
       *
       * A caller asking for certainty gets a hedge, and nothing anywhere says
       * so. Refused rather than zero-filled — the other remedy they offer —
       * because `0044` names *"silently repaired invalid input"* as a contract
       * failure in its own right, and because a prior that omits a value is
       * ambiguous rather than wrong: `{ dense: 1 }` could mean "the rest are
       * impossible" or "I have no opinion about the rest", and the spread
       * quietly picks the second. Refusing makes the caller say which.
       *
       * `aggregate()` fills every domain value, so nothing that came from the
       * pool can reach this.
       */
      const named = Object.keys(distribution);
      if (named.length !== domain.length) {
        throw new InvalidPoolPrior(knob, '',
          `${named.length} of ${domain.length} values named`);
      }
    }

    const global: Scope = { level: 'global' };
    for (const knob of Object.keys(opts.poolPrior)) {
      if (!POOLED.has(knob) || !isKnob(knob) || isIndexed(knob)) continue;
      const key = claimKey(knob, global);
      if (buckets.has(key)) continue;
      buckets.set(key, { knob, scope: global, b: emptyBucket(domainOf(knob)) });
    }
  }

  // Resolve posteriors, shallowest scope first, so each level can act as the
  // prior for the one below it.
  const order: ScopeLevel[] = ['global', 'context', 'surface'];
  const claims = new Map<string, Claim>();

  for (const level of order) {
    for (const [key, { knob, index, scope, b }] of buckets) {
      if (scope.level !== level) continue;
      const domain = domainOf(knob);
      const m = PRIOR_MASS[level];

      // Parent posterior, or the pool's, or uniform.
      const parentScope = scopeChain(scope)[1];
      const parent = parentScope
        ? claims.get(claimKey(knob, parentScope, index))?.posterior
        : undefined;
      /**
       * The pool may only be the base for a knob the pool may VOTE on.
       *
       * @Codex, P2 on #184: *"When `poolPrior` contains a live but forbidden
       * knob such as `collection.flow` and the ledger has already created a
       * bucket for that knob, this line still selects the supplied pool
       * distribution as the bucket's base even though the earlier pool-only
       * bucket loop skips it. An external or corrupted prior can therefore
       * influence the person's flow posterior despite decision `0040` allowing
       * pool votes only for density, typography, and palette."*
       *
       * Right, and it is the same guard tested in two places with two different
       * questions: the bucketing loop above asks `POOLED.has(knob)` before
       * MAKING a bucket, and this asked nothing before FILLING one. A knob the
       * ledger already mentions gets its bucket for its own reasons, and the
       * pool then walked in through the back of it.
       *
       * `0040` names the three knobs. This is that rule, applied at the second
       * place the prior is read rather than only at the first.
       */
      const pooled = POOLED.has(knob) ? opts.poolPrior?.[knob] : undefined;
      const base = parent ?? pooled ?? uniform(domain);
      const baseNorm = normalize({ ...uniform(domain), ...base });

      const positives = Object.values(b.weights).reduce((a, w) => a + Math.max(0, w), 0);
      const scores: Record<string, number> = {};
      for (const v of domain) {
        const alpha = m * (baseNorm[v] ?? 0);
        const w = b.weights[v] ?? 0;
        scores[v] = (Math.max(0, w) + alpha + Math.min(0, w)) / (positives + m);
      }
      const posterior = normalize(scores);

      const evidence = Object.values(b.weights).reduce((a, w) => a + Math.abs(w), 0);
      const hMax = Math.log(domain.length);
      const confidence =
        (hMax > 0 ? 1 - entropy(posterior) / hMax : 1) *
        (evidence / (evidence + m)) *
        Math.min(1, b.classes.size / 2) *
        reliability;

      claims.set(key, {
        knob, ...(index ? { index } : {}), scope,
        posterior, evidence,
        classes: [...b.classes], sessions: b.sessions.size, days: b.days.size,
        confidence, stated: b.stated, frozen: b.frozen,
        refractory: b.refractory,
        because: b.because.slice(-5),
      });
    }
  }

  return claims;
}

/** Resolve a knob for a surface, walking up the hierarchy until something has
 *  evidence. A context used twice a year never accumulates enough on its own,
 *  and should quietly inherit rather than guess. */
export function resolve(
  claims: Claims, knob: KnobId, scope: Scope, index?: string,
): Claim | undefined {
  for (const s of scopeChain(scope)) {
    const c = claims.get(claimKey(knob, s, index));
    // No proposals at surface scope on thin evidence — inherit instead.
    if (c && (s.level !== 'surface' || c.evidence >= 4)) return c;
  }
  return undefined;
}

export const argmax = (p: Record<string, number>): string =>
  Object.entries(p).sort((a, b) => b[1] - a[1])[0]![0];

/**
 * How far apart two posteriors may be and still be the same number.
 *
 * **Scaled to the arithmetic, not to a decimal place.** @Codex, P2 on #184:
 *
 * > *"For two valid one-genome votes scored `0.5000000005` and `0.5`,
 * > normalization leaves a real but sub-`1e-9` difference, so this tolerance
 * > incorrectly returns no winner. Decision 0040 permits any finite score in
 * > `[0,1]`, requires their sums to determine the distribution, and says only
 * > equal totals remain tied; limit the tolerance to arithmetic error
 * > attributable to summation rather than suppressing valid unequal inputs."*
 *
 * Correct, and the flat `1e-9` was a decimal place picked because it looked
 * fine — coarser than the artefact it was for and, as they show, coarser than
 * a difference somebody genuinely voted. Between those two facts there is no
 * single constant that is right; the width has to come from the computation.
 *
 * It does. Summing `n` values each at most `m` and dividing accumulates at most
 * about `n` roundings of half an ulp, so `n * EPSILON * m` bounds what addition
 * can have invented. For a posterior — `n` claims, all in `[0,1]` — that is a
 * few times `2.2e-16`, roughly a millionth of the old constant, and a
 * `5e-10` difference somebody voted survives it easily.
 *
 * `MIN_TIE_TOLERANCE` is the floor for the degenerate case of one or two
 * entries, where `n * EPSILON` would round to something narrower than a single
 * subtraction's own error.
 */
const MIN_TIE_TOLERANCE = 8 * Number.EPSILON;

/**
 * `summands` is how many numbers were ADDED to make these, not how many there
 * are. @Codex, P2 on #184, twice on this line:
 *
 * > *"this uses the number of posterior domain values — normally three — as
 * > `n`"* … and then, after the sorted-summand repair: *"10,000 scores of
 * > `0.1` plus 10,000 of `0.2` total the same mathematically as 10,000 scores
 * > of `0.3`, but this reducer produces totals differing by about `8.3e-10`."*
 *
 * The first is plainly right and was the bug: three is the domain size, and the
 * error accumulates once per vote.
 *
 * The second names a real gap and its suggested remedy does not close it.
 * Compensated or exact summation would report those two totals as **different**,
 * because they are: over the float inputs, `10000×float(0.1) + 10000×float(0.2)`
 * exceeds `10000×float(0.3)` by exactly `2.78e-13` before any addition happens —
 * `float(0.1) + float(0.2)` is already not `float(0.3)`. Only their *other*
 * suggestion works, which is this one: a bound that scales with the number of
 * summands. `n · EPSILON · max` with n = 20,000 is `4.4e-12`, which covers it.
 *
 * Sorting stays. It makes two equal MULTISETS bit-identical, which no tolerance
 * should have to paper over; this covers equal TOTALS reached from different
 * multisets, which sorting cannot.
 */
export const tieTolerance = (
  p: Record<string, number>, summands = Object.values(p).length,
): number => {
  const values = Object.values(p);
  const largest = values.reduce((a, b) => Math.max(a, Math.abs(b)), 0);
  return Math.max(MIN_TIE_TOLERANCE,
    Math.max(summands, values.length) * Number.EPSILON * largest);
};

/**
 * The old flat width, kept only so a caller that wants to talk about "a
 * difference too small to be evidence" in a message has a number to name. It
 * is NOT what `uniqueArgmax` compares against any more.
 */
export const TIE_TOLERANCE = 1e-9;

/** Return the sole best-supported value, or refuse to manufacture a winner.
 * Domain insertion order is serialization, not evidence: a flat or tied
 * posterior must therefore remain unresolved. */
export const uniqueArgmax = (
  p: Record<string, number>, summands?: number,
): string | undefined => {
  const ranked = Object.entries(p).sort((a, b) => b[1] - a[1]);
  if (!ranked.length) return undefined;
  const second = ranked[1]?.[1];
  if (second !== undefined
    && Math.abs(ranked[0]![1] - second) <= tieTolerance(p, summands)) {
    return undefined;
  }
  return ranked[0]![0];
};

export { scopeId };
